import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SectionHeading } from "./SectionHeading";
import { FramedScreenshot } from "./FramedScreenshot";
import { Lightbox } from "./Lightbox";
import { useTheme } from "../../contexts/ThemeContext";
import {
  TOUR_SHOTS,
  TOUR_SHOT_HEIGHT,
  TOUR_SHOT_WIDTH,
  tourShotSrc,
  tourShotSrcSet,
  type TourLang,
  type TourShotKey,
} from "./tourShots";

/** Product-tour section — real dashboard captures from the showcase tenants,
 * one large framed shot with a segmented tab selector. The image follows the
 * visitor's language (EN/ES capture) and theme (light/dark capture). Renders
 * statically like the rest of the landing (no scroll-triggered reveals — see
 * Capabilities.tsx). */
export function ProductTour() {
  const { t, i18n } = useTranslation();
  const lang = ((i18n.language ?? "en").split("-")[0] === "es" ? "es" : "en") as TourLang;
  const { theme } = useTheme();
  const [active, setActive] = useState<TourShotKey>("overview");
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const tabRefs = useRef<Partial<Record<TourShotKey, HTMLButtonElement | null>>>({});

  // Once the section scrolls into view, warm the caches that make switching
  // instant: the other shots for the current lang/theme and the opposite-theme
  // variant of every shot (so a theme toggle never flashes an empty frame).
  useEffect(() => {
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        observer.disconnect();
        const other = theme === "dark" ? "light" : "dark";
        for (const shot of TOUR_SHOTS) {
          new Image().src = tourShotSrc(shot.key, lang, theme);
          new Image().src = tourShotSrc(shot.key, lang, other);
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [lang, theme]);

  const onTabKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "ArrowRight" && e.key !== "ArrowLeft") return;
    e.preventDefault();
    const idx = TOUR_SHOTS.findIndex((s) => s.key === active);
    const delta = e.key === "ArrowRight" ? 1 : -1;
    const next = TOUR_SHOTS[(idx + delta + TOUR_SHOTS.length) % TOUR_SHOTS.length].key;
    setActive(next);
    tabRefs.current[next]?.focus();
  };

  return (
    <section
      ref={sectionRef}
      id="product-tour"
      className="relative scroll-mt-16 border-t border-slate-200/80 bg-white py-20 dark:border-white/5 dark:bg-slate-950 sm:py-28"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <SectionHeading
          tag={t("landing.tour.tag")}
          title={t("landing.tour.title")}
          subtitle={t("landing.tour.subtitle")}
        />

        {/* Segmented shot selector — pills wrap onto extra rows on narrow screens. */}
        <div
          role="tablist"
          aria-label={t("landing.tour.tag")}
          onKeyDown={onTabKeyDown}
          className="mb-8 flex flex-wrap justify-center gap-2"
        >
          {TOUR_SHOTS.map(({ key, icon: Icon }) => {
            const selected = key === active;
            return (
              <button
                key={key}
                ref={(el) => {
                  tabRefs.current[key] = el;
                }}
                role="tab"
                type="button"
                id={`tour-tab-${key}`}
                aria-selected={selected}
                aria-controls={`tour-panel-${key}`}
                tabIndex={selected ? 0 : -1}
                onClick={() => setActive(key)}
                className={`flex flex-none items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold ring-1 transition-colors ${
                  selected
                    ? "bg-slate-900 text-white ring-slate-900 dark:bg-white dark:text-slate-900 dark:ring-white"
                    : "bg-white text-slate-600 ring-slate-200 hover:text-slate-900 hover:ring-slate-300 dark:bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:text-white dark:hover:ring-white/20"
                }`}
              >
                <Icon size={15} />
                {t(`landing.tour.shots.${key}.label`)}
              </button>
            );
          })}
        </div>

        <div
          role="tabpanel"
          id={`tour-panel-${active}`}
          aria-labelledby={`tour-tab-${active}`}
        >
          <FramedScreenshot
            src={tourShotSrc(active, lang, theme)}
            srcSet={tourShotSrcSet(active, lang, theme)}
            alt={t(`landing.tour.shots.${active}.alt`)}
            width={TOUR_SHOT_WIDTH}
            height={TOUR_SHOT_HEIGHT}
            onExpand={() => setLightboxOpen(true)}
            expandLabel={t("landing.tour.expand")}
          />
          <div className="mx-auto mt-8 max-w-2xl text-center">
            <h3 className="text-lg font-semibold text-slate-900 dark:text-white">
              {t(`landing.tour.shots.${active}.title`)}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-400">
              {t(`landing.tour.shots.${active}.body`)}
            </p>
          </div>
        </div>
      </div>

      <Lightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        src={tourShotSrc(active, lang, theme, true)}
        alt={t(`landing.tour.shots.${active}.alt`)}
        closeLabel={t("landing.tour.close")}
      />
    </section>
  );
}
